/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { Download, FileText, Trash2 } from 'lucide-react';
import { useCallback, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { type TherapistDocument } from '../../lib/api/therapist-documents.js';
import { formatIst } from '../../lib/utils/datetime.js';
import { Badge } from '../ui/badge.js';
import { Button } from '../ui/button.js';

interface ItemProps {
  readonly doc: TherapistDocument;
  readonly onDownload: (doc: TherapistDocument) => void;
  readonly onDelete?: ((id: string) => void) | undefined;
}

function DocumentItem({ doc, onDownload, onDelete }: ItemProps): JSX.Element {
  const { t } = useTranslation();
  const download = useCallback(() => {
    onDownload(doc);
  }, [onDownload, doc]);
  const remove = useCallback(() => {
    onDelete?.(doc.id);
  }, [onDelete, doc.id]);
  return (
    <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900">
      <span className="bg-[var(--accent)]/10 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-[var(--accent-strong)]">
        <FileText className="h-4 w-4" aria-hidden="true" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="truncate text-sm font-medium text-slate-800 dark:text-white">{doc.title}</p>
          <Badge label={t(`documents.type.${doc.docType}`)} tone="violet" />
          {doc.supersededAt !== null && <Badge label={t('documents.superseded')} tone="slate" />}
        </div>
        <p className="truncate text-xs text-slate-400">
          {doc.fileName} · {t('documents.version', { n: doc.version })} ·{' '}
          {formatIst(doc.uploadedAt, 'dd MMM yyyy')}
        </p>
      </div>
      <Button type="button" variant="outline" onClick={download}>
        <Download className="mr-1.5 h-4 w-4" aria-hidden="true" />
        {t('documents.download')}
      </Button>
      {onDelete !== undefined && (
        <button
          type="button"
          onClick={remove}
          aria-label={t('documents.delete')}
          className="focus-ring rounded-lg p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-500 dark:hover:bg-rose-500/10"
        >
          <Trash2 className="h-4 w-4" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

interface ListProps {
  readonly docs: readonly TherapistDocument[];
  readonly onDownload: (doc: TherapistDocument) => void;
  readonly onDelete?: ((id: string) => void) | undefined;
}

/** A list of therapist documents with download (and optional delete) actions. */
export function DocumentList({ docs, onDownload, onDelete }: ListProps): JSX.Element {
  const { t } = useTranslation();
  if (docs.length === 0) {
    return <p className="text-sm text-slate-400">{t('documents.empty')}</p>;
  }
  return (
    <div className="space-y-2">
      {docs.map((doc) => (
        <DocumentItem key={doc.id} doc={doc} onDownload={onDownload} onDelete={onDelete} />
      ))}
    </div>
  );
}
